// ─────────────────────────────────────────────────────────────
//  Planning & Budgeting – Service Layer
//  Matches existing project service pattern exactly.
//  All endpoints should be updated to match your API base URL.
// ─────────────────────────────────────────────────────────────

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://your-api-base-url";

const defaultHeaders = {
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
  },
};

// ── Execute raw query (same as Fetchexecutequery) ──────────────
export const PBFetchexecutequery = () => [
  defaultHeaders,
  `${BASE_URL}/api/execute-query`,
];

// ── Fetch header / field config (same as Fetchheaderdata) ─────
export const PBFetchheaderdata = () => [
  defaultHeaders,
  `${BASE_URL}/api/header-data`,
];



// ── Update record (same as Fetchupdatequery) ──────────────────
export const PBFetchupdatequery = () => [
  defaultHeaders,
  `${BASE_URL}/api/update`,
];

// ── Delete / soft-delete record ───────────────────────────────
export const PBDeleteRecord = () => [
  defaultHeaders,
  `${BASE_URL}/api/delete`,
];

// ── File upload ───────────────────────────────────────────────
export const PBFetchfileupload = () => [
  defaultHeaders,
  `${BASE_URL}/api/file-upload`,
];

// ─────────────────────────────────────────────────────────────
//  SQL query builders – one per sub-module
//  Replace table/schema names to match your DB.
// ─────────────────────────────────────────────────────────────

export const Queries = {
  // ── PLANNING ───────────────────────────────────────────────

  getStrategicPlans: (entityId) =>
    `SELECT id, plan_name, plan_year, plan_type, kpi_target, status,
            created_by, DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.strategic_plan
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getOperationalPlans: (entityId) =>
    `SELECT id, department, plan_year, plan_quarter, description,
            target_value, uom, owner, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.operational_plan
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getWorkforcePlans: (entityId) =>
    `SELECT id, department, role, plan_year, current_headcount,
            planned_headcount, variance, cost_per_head, total_cost, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.workforce_plan
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getCapitalPlans: (entityId) =>
    `SELECT id, project_name, asset_type, plan_year, estimated_cost,
            approved_cost, roi_percent, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.capital_plan
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getScenarios: (entityId) =>
    `SELECT id, scenario_name, scenario_type, base_value,
            best_case_value, worst_case_value, probability_percent,
            risk_notes, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.scenario_plan
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  // ── BUDGETING ──────────────────────────────────────────────

  getOperatingBudgets: (entityId) =>
    `SELECT id, department, cost_center, expense_category,
            budget_year, budget_month,
            IFNULL(CONVERT(budget_amount,CHAR),'0') AS budget_amount,
            IFNULL(CONVERT(actual_amount,CHAR),'0') AS actual_amount,
            IFNULL(CONVERT(variance_amount,CHAR),'0') AS variance_amount,
            variance_percent, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.operating_budget
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getCapitalBudgets: (entityId) =>
    `SELECT id, project_name, asset_type, budget_year,
            IFNULL(CONVERT(approved_amount,CHAR),'0') AS approved_amount,
            IFNULL(CONVERT(spent_amount,CHAR),'0') AS spent_amount,
            IFNULL(CONVERT(remaining_amount,CHAR),'0') AS remaining_amount,
            depreciation_years, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.capital_budget
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getRevenueBudgets: (entityId) =>
    `SELECT id, product_line, region, channel, budget_year, budget_month,
            IFNULL(CONVERT(target_revenue,CHAR),'0') AS target_revenue,
            IFNULL(CONVERT(actual_revenue,CHAR),'0') AS actual_revenue,
            IFNULL(CONVERT(variance,CHAR),'0') AS variance,
            discount_percent, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.revenue_budget
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getPersonnelBudgets: (entityId) =>
    `SELECT id, department, grade_band, budget_year,
            IFNULL(CONVERT(headcount,CHAR),'0') AS headcount,
            IFNULL(CONVERT(salary_budget,CHAR),'0') AS salary_budget,
            IFNULL(CONVERT(benefits_budget,CHAR),'0') AS benefits_budget,
            IFNULL(CONVERT(bonus_budget,CHAR),'0') AS bonus_budget,
            IFNULL(CONVERT(training_budget,CHAR),'0') AS training_budget,
            IFNULL(CONVERT(total_budget,CHAR),'0') AS total_budget,
            status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.personnel_budget
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,

  getBudgetConsolidation: (entityId) =>
    `SELECT id, budget_year, entity_name, budget_type,
            IFNULL(CONVERT(total_opex,CHAR),'0') AS total_opex,
            IFNULL(CONVERT(total_capex,CHAR),'0') AS total_capex,
            IFNULL(CONVERT(total_revenue,CHAR),'0') AS total_revenue,
            IFNULL(CONVERT(total_personnel,CHAR),'0') AS total_personnel,
            IFNULL(CONVERT(grand_total,CHAR),'0') AS grand_total,
            version, approved_by, status,
            DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
     FROM planning_budgeting.budget_consolidation
     WHERE entity_id = ${entityId} AND is_active = 1
     ORDER BY id DESC`,
};

// ═══════════════════════════════════════════════════════════════
//  Planning & Budgeting – Enterprise Service Layer
//  Matches existing project pattern: returns [headers, url]
// ═══════════════════════════════════════════════════════════════

const BASE = process.env.REACT_APP_API_BASE_URL || "http://your-api-base-url";

const headers = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
  },
});

export const PBExecuteQuery  = () => [headers(), `${BASE}/api/execute-query`];
export const PBCreateData    = () => [headers(), `${BASE}/api/create`];
export const PBUpdateData    = () => [headers(), `${BASE}/api/update`];
export const PBDeleteData    = () => [headers(), `${BASE}/api/delete`];

// ── SQL query builders ─────────────────────────────────────────
export const Q = {

  // CEO Dashboard
  companyKPIs: (eid) => `
    SELECT kpi_name, kpi_value, kpi_target, kpi_unit, trend_direction,
           variance_percent, period, department
    FROM planning_budgeting.company_kpi
    WHERE entity_id=${eid} AND is_active=1
    ORDER BY sort_order ASC`,

  revenueByMonth: (eid, year) => `
    SELECT budget_month, SUM(target_revenue) AS target,
           SUM(actual_revenue) AS actual,
           SUM(forecast_revenue) AS forecast
    FROM planning_budgeting.revenue_budget
    WHERE entity_id=${eid} AND budget_year=${year} AND is_active=1
    GROUP BY budget_month ORDER BY FIELD(budget_month,
      'January','February','March','April','May','June',
      'July','August','September','October','November','December')`,

  plSummary: (eid, year) => `
    SELECT category, SUM(budget_amount) AS budget,
           SUM(actual_amount) AS actual,
           SUM(forecast_amount) AS forecast
    FROM planning_budgeting.pl_summary
    WHERE entity_id=${eid} AND budget_year=${year} AND is_active=1
    GROUP BY category ORDER BY sort_order`,

  // CFO Dashboard
  cashFlowMonthly: (eid, year) => `
    SELECT cf_month, inflow, outflow, (inflow-outflow) AS net_cashflow,
           opening_balance, closing_balance
    FROM planning_budgeting.cash_flow
    WHERE entity_id=${eid} AND cf_year=${year} AND is_active=1
    ORDER BY month_num ASC`,

  budgetVsActual: (eid, year) => `
    SELECT department, expense_category,
           SUM(budget_amount) AS budget,
           SUM(actual_amount) AS actual,
           SUM(budget_amount - actual_amount) AS variance,
           ROUND((SUM(budget_amount - actual_amount)/NULLIF(SUM(budget_amount),0))*100,2) AS variance_pct
    FROM planning_budgeting.operating_budget
    WHERE entity_id=${eid} AND budget_year=${year} AND is_active=1
    GROUP BY department, expense_category`,

  varianceWaterfall: (eid, year) => `
    SELECT category, budget_amount, actual_amount,
           (budget_amount - actual_amount) AS variance,
           waterfall_type
    FROM planning_budgeting.variance_waterfall
    WHERE entity_id=${eid} AND budget_year=${year} AND is_active=1
    ORDER BY sort_order`,

  // COO Dashboard
  workforceByDept: (eid, year) => `
    SELECT department,
           SUM(current_headcount) AS current_hc,
           SUM(planned_headcount) AS planned_hc,
           SUM(total_cost) AS total_cost,
           AVG(utilization_percent) AS utilization
    FROM planning_budgeting.workforce_plan
    WHERE entity_id=${eid} AND plan_year=${year} AND is_active=1
    GROUP BY department`,

  capexUtilization: (eid, year) => `
    SELECT project_name, asset_type, department,
           approved_amount, spent_amount,
           ROUND((spent_amount/NULLIF(approved_amount,0))*100,2) AS utilization_pct,
           status
    FROM planning_budgeting.capital_budget
    WHERE entity_id=${eid} AND budget_year=${year} AND is_active=1`,

  deptSpendHeatmap: (eid, year) => `
    SELECT department, budget_month,
           SUM(actual_amount) AS spend,
           SUM(budget_amount) AS budget,
           ROUND((SUM(actual_amount)/NULLIF(SUM(budget_amount),0))*100,2) AS utilization_pct
    FROM planning_budgeting.operating_budget
    WHERE entity_id=${eid} AND budget_year=${year} AND is_active=1
    GROUP BY department, budget_month`,

  // Revenue Modelling
  revenueDrivers: (eid) => `
    SELECT id, product_line, region, channel,
           unit_price, volume, mix_percent, discount_percent,
           ROUND(unit_price * volume * (mix_percent/100) * (1 - discount_percent/100),2) AS calculated_revenue,
           budget_year, status
    FROM planning_budgeting.revenue_driver
    WHERE entity_id=${eid} AND is_active=1
    ORDER BY id DESC`,

  // Rolling Forecast
  rollingForecast: (eid) => `
    SELECT forecast_month, forecast_year,
           revenue_forecast, expense_forecast,
           ebitda_forecast, confidence_low, confidence_high,
           actual_revenue, actual_expense,
           forecast_type
    FROM planning_budgeting.rolling_forecast
    WHERE entity_id=${eid} AND is_active=1
    ORDER BY forecast_year, month_num ASC
    LIMIT 18`,

  // Approval Workflow
  pendingApprovals: (eid, userId) => `
    SELECT w.id, w.module_name, w.record_id, w.submitted_by,
           w.submitted_on, w.current_approver_id,
           w.approval_level, w.status, w.comments,
           u.user_name AS submitted_by_name
    FROM planning_budgeting.approval_workflow w
    LEFT JOIN process_data.users u ON u.user_id = w.submitted_by
    WHERE w.entity_id=${eid}
      AND (w.current_approver_id=${userId} OR w.submitted_by=${userId})
      AND w.is_active=1
    ORDER BY w.submitted_on DESC`,

  scenarioList: (eid) => `
    SELECT id, scenario_name, scenario_type, plan_year,
           base_value, best_case_value, worst_case_value,
           probability_percent, risk_notes, status,
           DATE_FORMAT(created_on,'%d-%m-%Y') AS created_on
    FROM planning_budgeting.scenario_plan
    WHERE entity_id=${eid} AND is_active=1
    ORDER BY id DESC`,
};

// ── Utility ────────────────────────────────────────────────────
export const fmtDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

export const userMeta = (uc) => ({
  entity_id:       uc.selectedentity,
  tenant_id:       uc.tenants_id,
  group_id:        uc.groups_id,
  created_by:      uc.user_id,
  modified_by:     uc.user_id,
  last_updated_by: uc.user_name,
  created_on:      fmtDate(),
  last_updated_on: fmtDate(),
  is_active:       1,
});